/**
 * FoxChat DOM control layer.
 *
 * Exposes a global `window.foxchat` that lets the chatbot drive the UI:
 * highlight, click, scrollTo, fillField, openDrawer. Targets are addressed
 * by `data-foxchat-id` so IDs survive refactors better than CSS selectors.
 *
 * When a requested ID is not in the current DOM, we look it up in the
 * foxchat_kb table (populated during DOM scrape), navigate via React Router
 * (no full-page reload), then retry the action after hydration.
 */
import type { NavigateFunction } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { startWalkthrough, type Walkthrough } from "@/lib/foxchat-walkthrough";

export interface FoxChatAPI {
  highlight: (id: string) => Promise<boolean>;
  click: (id: string) => Promise<boolean>;
  scrollTo: (id: string) => Promise<boolean>;
  fillField: (id: string, value: string) => Promise<boolean>;
  openDrawer: (id: string) => Promise<boolean>;
  /** Kick off a multi-step guided flow. Returns a cleanup fn to stop early. */
  startWalkthrough: (wt: Walkthrough) => Promise<() => void>;
  /** Highlight every id in order with a short gap so the user sees the path. */
  highlightSequence: (ids: string[]) => Promise<void>;
  /** Run `[highlight: foo]` / `[click: foo]` markers inside an LLM reply. */
  handleDirectives: (text: string) => Promise<string>;
}

let navigator: NavigateFunction | null = null;

/** React layer calls this once inside a component so we have a router handle. */
export function registerNavigate(nav: NavigateFunction) {
  navigator = nav;
}

function query(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-foxchat-id="${CSS.escape(id)}"]`);
}

async function lookupPageUrl(id: string): Promise<string | null> {
  try {
    const { data } = await supabase
      .from("foxchat_kb" as any)
      .select("page_url")
      .eq("foxchat_id", id)
      .maybeSingle();
    return (data as any)?.page_url || null;
  } catch {
    return null;
  }
}

/** Wait until the element is in the DOM, polling every 120ms up to `timeoutMs`. */
function waitForElement(id: string, timeoutMs = 6000): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const existing = query(id);
    if (existing) return resolve(existing);
    const start = Date.now();
    const interval = setInterval(() => {
      const el = query(id);
      if (el) {
        clearInterval(interval);
        resolve(el);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        resolve(null);
      }
    }, 120);
  });
}

async function resolve(id: string): Promise<HTMLElement | null> {
  const local = query(id);
  if (local) return local;
  const pageUrl = await lookupPageUrl(id);
  if (pageUrl && navigator) {
    navigator(pageUrl);
    return waitForElement(id);
  }
  return null;
}

function flashRing(el: HTMLElement) {
  el.classList.add("foxchat-pulse-ring");
  const scroll = () => el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Start the scroll before the pulse so user sees what's being highlighted.
  requestAnimationFrame(scroll);
  window.setTimeout(() => el.classList.remove("foxchat-pulse-ring"), 3100);
}

export const foxchat: FoxChatAPI = {
  async highlight(id) {
    const el = await resolve(id);
    if (!el) return false;
    flashRing(el);
    return true;
  },
  async scrollTo(id) {
    const el = await resolve(id);
    if (!el) return false;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  },
  async click(id) {
    const el = await resolve(id);
    if (!el) return false;
    flashRing(el);
    // Brief delay so the user can see which element is about to fire.
    await new Promise((r) => setTimeout(r, 400));
    (el as HTMLElement).click();
    return true;
  },
  async fillField(id, value) {
    const el = await resolve(id);
    if (!el) return false;
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return false;
    // React controlled inputs need the native setter + an input event to update state.
    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    setter?.call(el, value);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    flashRing(el);
    return true;
  },
  async openDrawer(id) {
    // Drawers in this app are opened by clicking a trigger, so openDrawer === click.
    return foxchat.click(id);
  },

  async startWalkthrough(wt) {
    return startWalkthrough(wt);
  },

  async highlightSequence(ids) {
    for (const id of ids || []) {
      // Highlight one at a time (scrollTo lives inside highlight). resolve()
      // may navigate between pages so we wait for each ring to appear before
      // kicking off the next. 500 ms gap matches the spec.
      await foxchat.highlight(id);
      await new Promise((r) => setTimeout(r, 500));
    }
  },

  async handleDirectives(text) {
    // Parse `[highlight: id]`, `[click: id]`, `[scrollTo: id]`, `[open: id]`,
    // or `[fill: id | value]` (pipe-separated) anywhere in the text.
    const re = /\[(highlight|click|scrollTo|scroll|open|openDrawer|fill)\s*:\s*([^\]]+?)\]/gi;
    const matches = [...text.matchAll(re)];
    for (const m of matches) {
      const action = m[1].toLowerCase();
      const arg = m[2].trim();
      try {
        if (action === "highlight") await foxchat.highlight(arg);
        else if (action === "click") await foxchat.click(arg);
        else if (action === "scroll" || action === "scrollto") await foxchat.scrollTo(arg);
        else if (action === "open" || action === "opendrawer") await foxchat.openDrawer(arg);
        else if (action === "fill") {
          const [id, ...rest] = arg.split("|").map((s) => s.trim());
          if (id && rest.length) await foxchat.fillField(id, rest.join("|"));
        }
      } catch {
        // Swallow — UX must never break on a bad directive.
      }
    }
    // Strip all directives from the user-visible reply.
    return text.replace(re, "").replace(/\s{2,}/g, " ").trim();
  },
};

export function installFoxChatGlobals() {
  if (typeof window === "undefined") return;
  // Augment (don't clobber): the embedded Foxy widget attaches
  // window.foxchat.startTour to the same global. Object.assign keeps whichever
  // side loaded first (widget startTour or these MF methods) so both coexist
  // regardless of script/React mount order.
  (window as any).foxchat = Object.assign((window as any).foxchat || {}, foxchat);
}
